import type { ReactNode } from 'react';

export interface TheorySection {
  id: string;
  title: string;
  content: ReactNode;
}

export const buildTheorySections = (): TheorySection[] => [
  {
    id: 'overview',
    title: 'Algebraic expressions and polynomials',
    content: (
      <>
        <p>
          In the GMAT Quantitative Reasoning section, Algebraic Expressions and Polynomials form
          the foundation for many problem types - from simple arithmetic manipulation to complex
          word problems.
        </p>
        <p>
          A strong grasp of these concepts allows you to move efficiently through both Problem
          Solving and Data Insights questions, often saving valuable time by simplifying what
          appears to be complicated algebra.
        </p>
      </>
    ),
  },
  {
    id: 'understanding',
    title: 'Understanding Algebraic Expressions',
    content: (
      <>
        <p>
          An algebraic expression is a mathematical phrase that combines numbers, variables, and
          arithmetic operations (like +, -, x, and /). Expressions do not include equality signs -
          that is what differentiates them from equations. For example,
        </p>
        <div className="mt-2 space-y-2 font-mono text-sm text-foreground">
          <div>3x^2 - 5x + 2</div>
          <div>is an expression, while</div>
          <div>3x^2 - 5x + 2 = 0</div>
          <div>is an equation.</div>
        </div>
        <p>
          GMAT problems often test your ability to simplify, factorize, and manipulate such
          expressions. Typical tasks include expanding brackets, combining like terms, rationalizing
          denominators, or recognizing patterns like the difference of squares:
        </p>
        <div className="mt-2 font-mono text-sm text-foreground">a^2 - b^2 = (a + b)(a - b)</div>
      </>
    ),
  },
  {
    id: 'working',
    title: 'Working with Polynomials',
    content: (
      <>
        <p>
          A polynomial is a special kind of algebraic expression where variables have non-negative
          integer powers. Examples include x^2 + 4x + 3 or 2y^3 - 7y + 5. You should be comfortable
          identifying the degree (the highest exponent), coefficients, and the constant term.
        </p>
        <p>Key GMAT skills involving polynomials include:</p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>Factoring: Breaking down expressions into simpler components, e.g.</li>
        </ul>
        <div className="mt-2 font-mono text-sm text-foreground">
          x^2 + 5x + 6 = (x + 2)(x + 3)
        </div>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>Substitution: Plugging in given values or simplifying based on variable relationships.</li>
          <li>
            Recognizing hidden patterns: For example, noticing that x^4 - 1 = (x^2 + 1)(x + 1)(x -
            1).
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'strategy',
    title: 'GMAT Strategy Tip',
    content: (
      <>
        <p>
          Never expand an expression unless it simplifies the problem. Often, the test rewards
          those who see structure rather than compute everything. For instance, if a question asks
          for (x + y)^2 - (x - y)^2, expanding both sides is unnecessary - recognize it as another
          difference of squares:
        </p>
        <div className="mt-2 font-mono text-sm text-foreground">
          [(x + y) + (x - y)] [(x + y) - (x - y)] = 4xy
        </div>
      </>
    ),
  },
  {
    id: 'traps',
    title: 'Common GMAT Traps',
    content: (
      <>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>Mixing up similar-looking terms, e.g. x^2 + y^2 != (x + y)^2.</li>
          <li>Forgetting that division by zero is undefined.</li>
          <li>Overcomplicating expressions that could be simplified by factoring or cancellation.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'why',
    title: 'Why It Matters',
    content: (
      <>
        <p>
          Algebraic fluency underpins many higher-level GMAT topics, including inequalities,
          functions, and coordinate geometry. Mastering expressions and polynomials helps you see
          through numerical clutter, identify shortcuts, and build confidence for tougher
          quantitative challenges.
        </p>
      </>
    ),
  },
];
